import React, { useCallback, useEffect, useMemo, useState } from "react";
import MypageLayout from "./MypageLayout";
import RoleDispatch from "./roleDispatch";
import PayslipAdminPage from "./PayslipAdminPage";
import ErrorBar from "./ErrorBar";
import Loader from "./Loader";
import useEscapeKey from "./useEscapeKey";
import PdfViewer from "./PdfViewer";
import { FOUNDED_YM, MONTHS_AHEAD } from "./dateUtil";

// 給与明細ページ（社員本人・セッション認証）。Porcelain テーマ。
// 表示は「年ごとに12ヶ月を4×3グリッド」。明細のある月だけクリックでき、モーダルにPDFを埋め込み表示する。
// 年の枠は明細0件でも常に「設立年(2023)〜当月+2ヶ月の年」を出す（経営者側 PayslipAdminPage と揃える）。
// 交付日は表示しない（会議2026-07で不要と確定）。
const API_URL = process.env.REACT_APP_API_URL || "";
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function fmtYM(ym) {
  if (!ym || ym.length !== 6) return ym || "-";
  return `${ym.slice(0, 4)}年${parseInt(ym.slice(4, 6), 10)}月`;
}

const IconDoc = () => (
  <svg className="mp-ps-doc" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" />
  </svg>
);
const IconGift = () => (
  <svg className="mp-ps-gift" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="8" width="18" height="4" rx="1" /><path d="M5 12v9h14v-9M12 8v13" />
    <path d="M12 8S11 4 8.5 4a2 2 0 0 0 0 4zM12 8s1-4 3.5-4a2 2 0 0 1 0 4z" />
  </svg>
);

function PayslipView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null); // モーダル表示中 { id, year_month, url }

  const fetchMine = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/payslips/mine/`, { credentials: "include" });
      if (res.status === 401) { setError("ログインが必要です。"); return; }
      if (res.status === 403) { setError("社員プロフィールがありません。管理者にお問い合わせください。"); return; }
      if (!res.ok) throw new Error("取得に失敗しました");
      const data = await res.json();
      setRows(data.results || []);
    } catch (e) {
      setError("給与明細の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  // 月タイルのクリック → 行が持つ閲覧URL（同一オリジンstream配信パス）でモーダル表示（追加取得なし）。
  const open = (p) => {
    setError("");
    setViewing({ id: p.id, year_month: p.year_month, url: p.url, kind: p.kind, label: p.label });
  };

  // Esc でモーダルを閉じる。
  useEscapeKey(() => setViewing(null), !!viewing);

  // 月次と賞与(kind==="bonus")を分ける。月次＝年ごと12マスグリッド、賞与＝年ごとに別セクション。
  const monthlyRows = useMemo(() => rows.filter((r) => r.kind !== "bonus"), [rows]);
  const bonusRows = useMemo(() => rows.filter((r) => r.kind === "bonus"), [rows]);

  // 月次: 年ごとに { 月: 明細 } へまとめる。
  const byYear = useMemo(() => {
    const map = {};
    monthlyRows.forEach((r) => {
      if (!r.year_month || r.year_month.length !== 6) return;
      const y = r.year_month.slice(0, 4);
      const m = parseInt(r.year_month.slice(4, 6), 10);
      if (!map[y]) map[y] = {};
      map[y][m] = r;
    });
    return map;
  }, [monthlyRows]);

  // 賞与: 年ごとに配列へまとめ、各年内は支給月の新しい順。
  const bonusByYear = useMemo(() => {
    const map = {};
    bonusRows.forEach((r) => {
      if (!r.year_month || r.year_month.length !== 6) return;
      const y = r.year_month.slice(0, 4);
      if (!map[y]) map[y] = [];
      map[y].push(r);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => Number(b.year_month) - Number(a.year_month)));
    return map;
  }, [bonusRows]);

  // 表示する年＝「設立年(FOUNDED_YM)〜当月+MONTHS_AHEADの年」を常に出し、明細のある年も併合。
  // これにより明細0件でも年月グリッドの枠が出る（アップロード済みの月だけ押せる・経営者側と同じ範囲）。
  const years = useMemo(() => {
    const set = new Set([...Object.keys(byYear), ...Object.keys(bonusByYear)]);
    const startY = parseInt(FOUNDED_YM.slice(0, 4), 10);
    const now = new Date();
    let endY = now.getFullYear();
    let endM = now.getMonth() + 1 + MONTHS_AHEAD; // 当月+MONTHS_AHEAD（年跨ぎは繰り上げ）
    while (endM > 12) { endM -= 12; endY += 1; }
    for (let y = startY; y <= endY; y += 1) set.add(String(y));
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [byYear, bonusByYear]);

  return (
    <MypageLayout active="payslip" title="給与明細">
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      {loading ? (
        <Loader />
      ) : (
        <div className="mp-ps">
          {years.map((y) => (
            <section className="mp-ps-year" key={y}>
              <div className="mp-ps-yhead">{y}年</div>
              <div className="mp-ps-grid">
                {MONTHS.map((m) => {
                  const p = (byYear[y] || {})[m];
                  if (!p) {
                    // 明細のない月は視覚的に沈める（月数字のみ・淡色）。場所を食わないよう装飾を省く。
                    return (
                      <div className="mp-ps-cell mp-ps-none" key={m}>
                        <span className="mp-ps-m">{m}月</span>
                      </div>
                    );
                  }
                  // 明細のある月はドキュメントアイコン＋月表示のみ。「明細あり」ラベルは
                  // クリック可能＝明細ありが自明のため撤去（全カード反復による冗長さを解消）。
                  return (
                    <button
                      className="mp-ps-cell"
                      key={m}
                      onClick={() => open(p)}
                    >
                      <span className="mp-ps-m">{m}月</span>
                      <IconDoc />
                    </button>
                  );
                })}
              </div>
              {bonusByYear[y] && bonusByYear[y].length > 0 ? (
                <div className="mp-ps-bonus">
                  <div className="mp-ps-bonus-head"><IconGift /> 賞与</div>
                  <div className="mp-ps-bonus-list">
                    {bonusByYear[y].map((p) => (
                      <button
                        className="mp-ps-bonus-tile"
                        key={p.id}
                        onClick={() => open(p)}
                      >
                        <IconDoc />
                        <span className="mp-ps-bonus-name">{p.label || "賞与"}</span>
                        <span className="mp-ps-bonus-m">{parseInt(p.year_month.slice(4, 6), 10)}月支給</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ))}
        </div>
      )}

      {/* PDF埋め込みモーダル。背景クリック / Esc / 閉じる で閉じる。 */}
      {viewing ? (
        <div className="mp-modal-back" onClick={() => setViewing(null)}>
          <div className="mp-ps-modal" role="dialog" aria-modal="true" aria-label="給与明細" onClick={(e) => e.stopPropagation()}>
            <div className="mp-ps-modal-head">
              <div className="mp-ps-modal-title">{viewing.kind === "bonus" ? `${viewing.year_month.slice(0, 4)}年 ${viewing.label || "賞与"}` : `${fmtYM(viewing.year_month)}の給与明細`}</div>
              <button className="mp-ps-modal-x" onClick={() => setViewing(null)} aria-label="閉じる">×</button>
            </div>
            {/* PDF.js でアプリ内描画（ツールバー無し・横幅フィット）。失敗時は下のボタンにフォールバック。 */}
            <PdfViewer url={viewing.url} />
            <div className="mp-ps-modal-foot">
              <button className="mp-btn mp-ghost mp-sm" onClick={() => window.open(viewing.url, "_blank", "noopener")}>新しいタブで開く</button>
              <a className="mp-btn mp-primary mp-sm" href={viewing.url} download={`給与明細_${viewing.year_month}.pdf`}>ダウンロード</a>
            </div>
          </div>
        </div>
      ) : null}
    </MypageLayout>
  );
}

// 役割で出し分け: 社員=自分の給与明細、経営者=給与明細の管理(アップロード)。メニュー名・ルートは不変。
function PayslipPage() {
  return <RoleDispatch active="payslip" title="給与明細" Employee={PayslipView} Executive={PayslipAdminPage} />;
}

export default PayslipPage;
